import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Users, MapPin, Plus, Phone, X, Truck, FileText } from "lucide-react";
import { api, API } from "../lib/api";

/**
 * Wedding & Special Events workspace for dispatchers.
 *
 * Lists all group-vehicle events (Wedding · Corporate · Prom · Funeral · Other)
 * with filter chips + per-event detail modal showing all linked vehicles,
 * client + POC info, and a quick "add another vehicle" action.
 */
const TYPE_COLORS = {
  wedding:   { bg: "bg-[#F5D0FE]/20", text: "text-[#F5D0FE]", border: "border-[#F5D0FE]/40" },
  corporate: { bg: "bg-[#93C5FD]/20", text: "text-[#93C5FD]", border: "border-[#93C5FD]/40" },
  prom:      { bg: "bg-[#FDE68A]/20", text: "text-[#FDE68A]", border: "border-[#FDE68A]/40" },
  funeral:   { bg: "bg-[#D4D4D8]/20", text: "text-[#D4D4D8]", border: "border-[#D4D4D8]/40" },
  other:     { bg: "bg-[#86EFAC]/20", text: "text-[#86EFAC]", border: "border-[#86EFAC]/40" },
};

const FILTERS = [
  ["all", "All"],
  ["wedding", "Weddings"],
  ["corporate", "Corporate"],
  ["prom", "Proms"],
  ["funeral", "Funerals"],
  ["other", "Other"],
];

export default function EventsTab() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async (t = filter) => {
    try {
      const r = await api.get("/admin/events", { params: { event_type: t } });
      setEvents(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load events");
    }
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  return (
    <section className="pb-20 px-6 md:px-12" data-testid="events-tab">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h2 className="font-serif text-2xl text-[#F5F2EB]">Wedding &amp; Special Events</h2>
            <p className="text-xs text-[#71717A] mt-1 uppercase tracking-[0.18em]">Group bookings · multiple vehicles per client</p>
          </div>
          <button
            data-testid="events-new-btn"
            onClick={() => setCreating(true)}
            className="btn-gold text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2"
          ><Plus size={13}/> New Event</button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="event-filters">
          {FILTERS.map(([k, l]) => (
            <button
              key={k}
              type="button"
              data-testid={`event-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${filter === k ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-white/15 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}
            >{l}</button>
          ))}
        </div>

        {events.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center text-sm text-[#71717A]" data-testid="events-empty">
            No {filter === "all" ? "" : filter + " "}events yet. Customers can book multi-vehicle events from the public site — you can also add one manually from a phone-in.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {events.map(evt => {
              const c = TYPE_COLORS[evt.event_type] || TYPE_COLORS.other;
              return (
                <button
                  key={evt.event_id}
                  data-testid={`event-card-${evt.event_id}`}
                  onClick={() => setSelected(evt)}
                  className="text-left border border-white/10 bg-[#0A0A0A] p-5 hover:border-[#D4AF37]/40 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${c.border} ${c.bg} ${c.text}`}>{evt.event_type}</span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] font-mono">{evt.event_code}</span>
                  </div>
                  <div className="font-serif text-lg text-[#F5F2EB] mb-1">{evt.event_name}</div>
                  <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center gap-1"><Calendar size={11}/>{evt.event_date}</span>
                    <span className="inline-flex items-center gap-1"><Users size={11}/>{evt.vehicle_count} vehicle{evt.vehicle_count === 1 ? "" : "s"}</span>
                  </div>
                  {evt.venue && (
                    <div className="text-xs text-[#71717A] inline-flex items-center gap-1 mb-2"><MapPin size={11}/>{evt.venue}</div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-white/5">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">{evt.customer_name}</span>
                    <span className="text-sm text-[#D4AF37] font-medium">${Number(evt.total_fare || 0).toFixed(2)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selected && <EventDetailModal event={selected} onClose={() => setSelected(null)} onChange={() => load(filter)}/>}
      {creating && <NewEventModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(filter); }}/>}
    </section>
  );
}

function EventDetailModal({ event, onClose, onChange }) {
  const [full, setFull] = useState(null);
  const [adding, setAdding] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [newVeh, setNewVeh] = useState({ vehicle_name: "", pickup_address: "", dropoff_address: "", pickup_datetime: "", pax: 1, luggage: 0, fare: 0 });
  const [timeline, setTimeline] = useState({ getting_ready_time: "", ceremony_time: "", reception_time: "" });

  const load = async () => {
    try {
      const r = await api.get(`/admin/events/${event.event_id}`);
      setFull(r.data);
      setTimeline({
        getting_ready_time: r.data.getting_ready_time || "",
        ceremony_time: r.data.ceremony_time || "",
        reception_time: r.data.reception_time || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load event");
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.event_id]);
  useEffect(() => {
    // Load drivers once — used by the per-vehicle assignment dropdowns.
    api.get("/drivers").then(r => setDrivers(r.data || [])).catch(() => setDrivers([]));
  }, []);

  const addVehicle = async () => {
    if (!newVeh.vehicle_name || !newVeh.pickup_datetime) {
      toast.error("Vehicle name and pickup time are required");
      return;
    }
    try {
      await api.post(`/admin/events/${event.event_id}/vehicles`, {
        ...newVeh,
        pax: Number(newVeh.pax) || 1,
        luggage: Number(newVeh.luggage) || 0,
        fare: Number(newVeh.fare) || 0,
      });
      await load();
      onChange();
      setAdding(false);
      setNewVeh({ vehicle_name: "", pickup_address: "", dropoff_address: "", pickup_datetime: "", pax: 1, luggage: 0, fare: 0 });
      toast.success("Vehicle added to event");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const assignDriver = async (bookingId, driverId) => {
    if (!driverId) return;
    const d = drivers.find(x => x.user_id === driverId || x.driver_id === driverId);
    if (!d) return;
    try {
      await api.post(`/admin/events/${event.event_id}/vehicles/${bookingId}/assign-driver`, {
        driver_id: d.user_id || d.driver_id,
        driver_name: d.name || d.driver_name || "Driver",
      });
      await load();
      toast.success(`Assigned ${d.name || d.driver_name} to vehicle`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    }
  };

  const saveTimeline = async () => {
    try {
      await api.patch(`/admin/events/${event.event_id}`, timeline);
      toast.success("Timeline saved");
      await load();
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (!full) return null;
  const c = TYPE_COLORS[full.event_type] || TYPE_COLORS.other;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="event-detail-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${c.border} ${c.bg} ${c.text}`}>{full.event_type}</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] font-mono">{full.event_code}</span>
            </div>
            <div className="font-serif text-2xl text-[#F5F2EB]">{full.event_name}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">
              {full.event_date}{full.venue ? ` · ${full.venue}` : ""}
            </div>
          </div>
          <button data-testid="event-modal-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>

        {/* Actions row — Run Sheet PDF for coordinators/drivers */}
        <div className="flex justify-end gap-2 px-6 py-3 border-b border-white/5">
          <a
            data-testid="event-run-sheet-btn"
            href={`${API}/admin/events/${event.event_id}/run-sheet.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            <FileText size={12}/> Print run sheet
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 py-5 border-b border-white/5">
          <div>
            <div className="overline mb-2">Client</div>
            <div className="text-sm text-[#F5F2EB]">{full.customer_name}</div>
            {full.customer_email && <div className="text-xs text-[#A1A1AA]">{full.customer_email}</div>}
            {full.customer_phone && <div className="text-xs text-[#A1A1AA] flex items-center gap-1"><Phone size={10}/>{full.customer_phone}</div>}
          </div>
          <div>
            <div className="overline mb-2">Point of Contact</div>
            <div className="text-sm text-[#F5F2EB]">{full.poc_name || "—"}</div>
            {full.poc_phone && <div className="text-xs text-[#A1A1AA] flex items-center gap-1"><Phone size={10}/>{full.poc_phone}</div>}
          </div>
        </div>

        {/* Event day timeline — for wedding-style events with distinct phases */}
        <div className="px-6 py-5 border-b border-white/5" data-testid="event-timeline">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Event day timeline</div>
            <button data-testid="event-timeline-save" onClick={saveTimeline} className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline">Save</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div>
              <label className="field-label">Getting ready</label>
              <input type="datetime-local" className="field-input" data-testid="timeline-getting-ready"
                value={timeline.getting_ready_time} onChange={e => setTimeline({ ...timeline, getting_ready_time: e.target.value })}/>
            </div>
            <div>
              <label className="field-label">Ceremony</label>
              <input type="datetime-local" className="field-input" data-testid="timeline-ceremony"
                value={timeline.ceremony_time} onChange={e => setTimeline({ ...timeline, ceremony_time: e.target.value })}/>
            </div>
            <div>
              <label className="field-label">Reception</label>
              <input type="datetime-local" className="field-input" data-testid="timeline-reception"
                value={timeline.reception_time} onChange={e => setTimeline({ ...timeline, reception_time: e.target.value })}/>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="flex justify-between items-center mb-3">
            <div className="overline">Vehicles · {full.vehicle_count}</div>
            <button data-testid="event-add-vehicle" onClick={() => setAdding(true)} className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline inline-flex items-center gap-1"><Plus size={12}/> Add vehicle</button>
          </div>
          <div className="space-y-2">
            {full.trips.map(t => (
              <div key={t.booking_id} className="border border-white/5 p-3" data-testid={`event-vehicle-${t.booking_id}`}>
                <div className="grid grid-cols-12 gap-3 items-center text-xs">
                  <div className="col-span-3 text-[#D4AF37] font-mono">{t.confirmation_code}</div>
                  <div className="col-span-3 text-[#F5F2EB] inline-flex items-center gap-1"><Truck size={11}/>{t.vehicle_name}</div>
                  <div className="col-span-3 text-[#A1A1AA]">{new Date(t.pickup_datetime).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}</div>
                  <div className="col-span-2 text-[#A1A1AA] truncate">{(t.pickup_address || "").split(",")[0]}</div>
                  <div className="col-span-1 text-right text-[#D4AF37]">${Number(t.fare || 0).toFixed(0)}</div>
                </div>
                {/* Driver assignment row */}
                <div className="grid grid-cols-12 gap-3 items-center text-xs mt-2 pt-2 border-t border-white/5">
                  <div className="col-span-3 text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Driver</div>
                  <div className="col-span-9">
                    <select
                      className="field-input text-xs"
                      data-testid={`event-vehicle-driver-${t.booking_id}`}
                      value={t.assigned_driver_id || ""}
                      onChange={(e) => assignDriver(t.booking_id, e.target.value)}
                    >
                      <option value="">— Unassigned —</option>
                      {drivers.map(d => (
                        <option key={d.user_id || d.driver_id} value={d.user_id || d.driver_id}>
                          {d.name || d.driver_name}
                        </option>
                      ))}
                    </select>
                    {t.assigned_driver_name && (
                      <div className="text-[10px] text-[#86EFAC] mt-1">✓ Assigned to {t.assigned_driver_name}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {adding && (
            <div className="mt-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 space-y-2" data-testid="event-add-vehicle-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input className="field-input" data-testid="new-veh-name" placeholder="Vehicle name (e.g. Sprinter)" value={newVeh.vehicle_name} onChange={e => setNewVeh({ ...newVeh, vehicle_name: e.target.value })}/>
                <input type="datetime-local" className="field-input" data-testid="new-veh-pickup" value={newVeh.pickup_datetime} onChange={e => setNewVeh({ ...newVeh, pickup_datetime: e.target.value })}/>
                <input className="field-input" data-testid="new-veh-pickup-addr" placeholder="Pickup address" value={newVeh.pickup_address} onChange={e => setNewVeh({ ...newVeh, pickup_address: e.target.value })}/>
                <input className="field-input" data-testid="new-veh-dropoff-addr" placeholder="Dropoff address" value={newVeh.dropoff_address} onChange={e => setNewVeh({ ...newVeh, dropoff_address: e.target.value })}/>
                <input type="number" className="field-input" data-testid="new-veh-pax" placeholder="PAX" value={newVeh.pax} onChange={e => setNewVeh({ ...newVeh, pax: e.target.value })}/>
                <input type="number" step="0.01" className="field-input" data-testid="new-veh-fare" placeholder="Fare ($)" value={newVeh.fare} onChange={e => setNewVeh({ ...newVeh, fare: e.target.value })}/>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button data-testid="new-veh-cancel" onClick={() => setAdding(false)} className="btn-ghost text-[11px]">Cancel</button>
                <button data-testid="new-veh-save" onClick={addVehicle} className="btn-gold text-[11px]">Add</button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-baseline px-6 py-4 border-t border-white/5">
          <span className="overline">Total (all vehicles)</span>
          <span className="font-serif text-2xl text-[#D4AF37]" data-testid="event-total">${Number(full.total_fare || 0).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * NewEventModal — compact dispatcher-side form to create a Wedding/Corporate/etc
 * group booking from a phone-in. Posts to the SAME public /events/group-book
 * endpoint the customer form uses so no divergent code paths.
 */
function NewEventModal({ onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    event_type: "wedding",
    event_name: "",
    event_date: "",
    venue: "",
    notes: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    poc_name: "",
    poc_phone: "",
  });
  const [vehicles, setVehicles] = useState([
    { vehicle_name: "Lux XL SUV", pickup_address: "", dropoff_address: "", pickup_datetime: "", pax: 4, luggage: 0, fare: 0 },
  ]);

  const addVehicle = () => setVehicles([...vehicles, {
    vehicle_name: "Luxury Sedan", pickup_address: "", dropoff_address: "",
    pickup_datetime: "", pax: 3, luggage: 0, fare: 0,
  }]);
  const removeVehicle = (idx) => setVehicles(vehicles.filter((_, i) => i !== idx));
  const updateVehicle = (idx, patch) => setVehicles(vehicles.map((v, i) => i === idx ? { ...v, ...patch } : v));

  const submit = async () => {
    if (!form.event_name || !form.event_date || !form.customer_name) {
      toast.error("Event name, date and client name are required");
      return;
    }
    if (!form.customer_email && !form.customer_phone) {
      toast.error("Client email or phone required for confirmations");
      return;
    }
    if (vehicles.some(v => !v.vehicle_name || !v.pickup_datetime)) {
      toast.error("Every vehicle needs a name and pickup time");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicles: vehicles.map(v => ({
          ...v,
          pax: Number(v.pax) || 1,
          luggage: Number(v.luggage) || 0,
          fare: Number(v.fare) || 0,
        })),
      };
      const r = await api.post("/events/group-book", payload);
      toast.success(`Event created · ${r.data.event_code}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="event-new-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
          <div>
            <div className="overline mb-1">New group booking</div>
            <div className="font-serif text-2xl text-[#F5F2EB]">Wedding &amp; Special Events</div>
            <div className="text-xs text-[#71717A] mt-1">Multi-vehicle group reservation from a phone-in.</div>
          </div>
          <button data-testid="event-new-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>

        {/* Event meta */}
        <div className="px-6 py-5 border-b border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <label className="field-label">Event type</label>
            <select className="field-input" data-testid="new-event-type" value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })}>
              <option value="wedding">Wedding</option>
              <option value="corporate">Corporate</option>
              <option value="prom">Prom</option>
              <option value="funeral">Funeral</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="field-label">Event date</label>
            <input type="date" className="field-input" data-testid="new-event-date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })}/>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Event name</label>
            <input className="field-input" data-testid="new-event-name" placeholder="e.g. Anderson Wedding" value={form.event_name} onChange={e => setForm({ ...form, event_name: e.target.value })}/>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Venue</label>
            <input className="field-input" data-testid="new-event-venue" placeholder="e.g. Pfister Hotel" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })}/>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Notes (optional)</label>
            <input className="field-input" data-testid="new-event-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}/>
          </div>
        </div>

        {/* Client + POC */}
        <div className="px-6 py-5 border-b border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="overline mb-2">Client</div>
            <input className="field-input mb-2" data-testid="new-event-customer-name" placeholder="Full name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })}/>
            <input className="field-input mb-2" data-testid="new-event-customer-email" placeholder="Email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })}/>
            <input className="field-input" data-testid="new-event-customer-phone" placeholder="Phone" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })}/>
          </div>
          <div>
            <div className="overline mb-2">Point of Contact (planner)</div>
            <input className="field-input mb-2" data-testid="new-event-poc-name" placeholder="Planner name" value={form.poc_name} onChange={e => setForm({ ...form, poc_name: e.target.value })}/>
            <input className="field-input" data-testid="new-event-poc-phone" placeholder="Planner phone" value={form.poc_phone} onChange={e => setForm({ ...form, poc_phone: e.target.value })}/>
          </div>
        </div>

        {/* Vehicles */}
        <div className="px-6 py-5 border-b border-white/5">
          <div className="flex justify-between items-center mb-3">
            <div className="overline">Vehicles · {vehicles.length}</div>
            <button data-testid="new-event-add-vehicle" onClick={addVehicle} className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline inline-flex items-center gap-1"><Plus size={12}/> Add vehicle</button>
          </div>
          <div className="space-y-3">
            {vehicles.map((v, idx) => (
              <div key={idx} className="border border-white/5 p-3" data-testid={`new-event-veh-${idx}`}>
                <div className="flex justify-between items-center mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Vehicle {idx + 1}</div>
                  {vehicles.length > 1 && (
                    <button data-testid={`new-event-veh-remove-${idx}`} onClick={() => removeVehicle(idx)} className="text-[10px] uppercase tracking-[0.22em] text-red-400 hover:underline">Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <select className="field-input" data-testid={`new-event-veh-name-${idx}`} value={v.vehicle_name} onChange={e => updateVehicle(idx, { vehicle_name: e.target.value })}>
                    <option>Luxury Sedan</option>
                    <option>Lux SUV</option>
                    <option>Lux XL SUV</option>
                    <option>Luxury SUV</option>
                    <option>Sprinter Van</option>
                  </select>
                  <input type="datetime-local" className="field-input" data-testid={`new-event-veh-pickup-${idx}`} value={v.pickup_datetime} onChange={e => updateVehicle(idx, { pickup_datetime: e.target.value })}/>
                  <input className="field-input" data-testid={`new-event-veh-pickup-addr-${idx}`} placeholder="Pickup address" value={v.pickup_address} onChange={e => updateVehicle(idx, { pickup_address: e.target.value })}/>
                  <input className="field-input" data-testid={`new-event-veh-dropoff-addr-${idx}`} placeholder="Dropoff address" value={v.dropoff_address} onChange={e => updateVehicle(idx, { dropoff_address: e.target.value })}/>
                  <input type="number" className="field-input" data-testid={`new-event-veh-pax-${idx}`} placeholder="PAX" value={v.pax} onChange={e => updateVehicle(idx, { pax: e.target.value })}/>
                  <input type="number" step="0.01" className="field-input" data-testid={`new-event-veh-fare-${idx}`} placeholder="Fare ($)" value={v.fare} onChange={e => updateVehicle(idx, { fare: e.target.value })}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4">
          <button data-testid="new-event-cancel" onClick={onClose} className="btn-ghost text-[11px]" disabled={saving}>Cancel</button>
          <button data-testid="new-event-submit" onClick={submit} className="btn-gold text-[11px]" disabled={saving}>
            {saving ? "Creating..." : "Create event"}
          </button>
        </div>
      </div>
    </div>
  );
}

